import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  ClockIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  PlusIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { schedulerAPI, formatError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Scheduler = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTask, setNewTask] = useState({
    task_name: '',
    schedule_type: 'daily',
    schedule_config: {
      hour: 9,
      minute: 0,
      day_of_week: 1
    },
    enabled: true,
    analysis_config: {
      analysis_period_days: 30
    }
  });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await schedulerAPI.getTasks();
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to load scheduled tasks');
      console.error('Load tasks error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createTask = async () => {
    try {
      await schedulerAPI.createTask(newTask);
      toast.success('Scheduled task created');
      setShowCreateForm(false);
      setNewTask({
        task_name: '',
        schedule_type: 'daily',
        schedule_config: {
          hour: 9,
          minute: 0,
          day_of_week: 1
        },
        enabled: true,
        analysis_config: {
          analysis_period_days: 30
        }
      });
      loadTasks();
    } catch (error) {
      toast.error(formatError(error));
    }
  };

  const toggleTask = async (taskId, enabled) => {
    try {
      await schedulerAPI.updateTask(taskId, { enabled });
      toast.success(`Task ${enabled ? 'enabled' : 'disabled'}`);
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled task?')) {
      return;
    }

    try {
      await schedulerAPI.deleteTask(taskId);
      toast.success('Task deleted');
      loadTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const runTaskNow = async (taskId) => {
    try {
      toast.loading('Running task...', { id: 'run-task' });
      await schedulerAPI.runTask(taskId);
      toast.success('Task executed successfully', { id: 'run-task' });
    } catch (error) {
      toast.error('Failed to run task', { id: 'run-task' });
    }
  };

  const getScheduleDisplay = (task) => {
    const config = task.schedule_config;
    
    switch (task.schedule_type) {
      case 'daily':
        return `Daily at ${String(config.hour).padStart(2, '0')}:${String(config.minute).padStart(2, '0')}`;
      
      case 'weekly':
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `Weekly on ${days[config.day_of_week]} at ${String(config.hour).padStart(2, '0')}:${String(config.minute).padStart(2, '0')}`;
      
      case 'monthly':
        return `Monthly on day ${config.day_of_month} at ${String(config.hour).padStart(2, '0')}:${String(config.minute).padStart(2, '0')}`;
      
      default:
        return 'Custom schedule';
    }
  };

  const getNextRun = (task) => {
    if (!task.next_run) return 'Not scheduled';
    
    const nextRun = new Date(task.next_run);
    const now = new Date();
    const diffMs = nextRun - now;
    
    if (diffMs < 0) return 'Overdue';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `In ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Scheduler</h1>
          <p className="text-gray-600 mt-1">Manage automated AI analysis tasks</p>
        </div>
        
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Task
        </button>
      </div>

      {/* Create Task Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Create Scheduled Task</h2>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Task Name
              </label>
              <input
                type="text"
                value={newTask.task_name}
                onChange={(e) => setNewTask({...newTask, task_name: e.target.value})}
                placeholder="e.g., Daily Inventory Analysis"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Type
              </label>
              <select
                value={newTask.schedule_type}
                onChange={(e) => {
                  const scheduleType = e.target.value;
                  let defaultConfig = { hour: 9, minute: 0 };
                  
                  if (scheduleType === 'weekly') {
                    defaultConfig.day_of_week = 1;
                  } else if (scheduleType === 'monthly') {
                    defaultConfig.day_of_month = 1;
                  }
                  
                  setNewTask({
                    ...newTask,
                    schedule_type: scheduleType,
                    schedule_config: defaultConfig
                  });
                }}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            
            <div className="flex space-x-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hour
                </label>
                <select
                  value={newTask.schedule_config.hour}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    schedule_config: {
                      ...newTask.schedule_config,
                      hour: parseInt(e.target.value)
                    }
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {Array.from({length: 24}, (_, i) => (
                    <option key={i} value={i}>
                      {String(i).padStart(2, '0')}:00
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minute
                </label>
                <select
                  value={newTask.schedule_config.minute}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    schedule_config: {
                      ...newTask.schedule_config,
                      minute: parseInt(e.target.value)
                    }
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value={0}>00</option>
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                </select>
              </div>
            </div>
            
            {newTask.schedule_type === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Week
                </label>
                <select
                  value={newTask.schedule_config.day_of_week}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    schedule_config: {
                      ...newTask.schedule_config,
                      day_of_week: parseInt(e.target.value)
                    }
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value={0}>Sunday</option>
                  <option value={1}>Monday</option>
                  <option value={2}>Tuesday</option>
                  <option value={3}>Wednesday</option>
                  <option value={4}>Thursday</option>
                  <option value={5}>Friday</option>
                  <option value={6}>Saturday</option>
                </select>
              </div>
            )}
            
            {newTask.schedule_type === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Day of Month
                </label>
                <select
                  value={newTask.schedule_config.day_of_month}
                  onChange={(e) => setNewTask({
                    ...newTask,
                    schedule_config: {
                      ...newTask.schedule_config,
                      day_of_month: parseInt(e.target.value)
                    }
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {Array.from({length: 28}, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Analysis Period (Days)
              </label>
              <select
                value={newTask.analysis_config.analysis_period_days}
                onChange={(e) => setNewTask({
                  ...newTask,
                  analysis_config: {
                    analysis_period_days: parseInt(e.target.value)
                  }
                })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={createTask}
              disabled={!newTask.task_name.trim()}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Task
            </button>
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Scheduled Tasks</h2>
        </div>
        
        {loading ? (
          <div className="p-6">
            <LoadingSpinner text="Loading tasks..." />
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No scheduled tasks</h3>
            <p className="text-gray-600 mb-4">
              Create your first scheduled task to automate AI analysis
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create First Task
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {tasks.map((task) => (
              <div key={task.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {task.task_name}
                      </h3>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        task.enabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {task.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    
                    <div className="mt-1 space-y-1">
                      <p className="text-sm text-gray-600">
                        <ClockIcon className="inline h-4 w-4 mr-1" />
                        {getScheduleDisplay(task)}
                      </p>
                      
                      <p className="text-sm text-gray-600">
                        Next run: {getNextRun(task)}
                      </p>
                      
                      {task.last_run && (
                        <p className="text-sm text-gray-600">
                          Last run: {new Date(task.last_run).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => runTaskNow(task.id)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md"
                      title="Run now"
                    >
                      <PlayIcon className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => toggleTask(task.id, !task.enabled)}
                      className={`p-2 rounded-md ${
                        task.enabled
                          ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                      }`}
                      title={task.enabled ? 'Disable' : 'Enable'}
                    >
                      {task.enabled ? (
                        <PauseIcon className="h-4 w-4" />
                      ) : (
                        <PlayIcon className="h-4 w-4" />
                      )}
                    </button>
                    
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                      title="Delete task"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Task Details */}
                <div className="mt-4 bg-gray-50 rounded-md p-3">
                  <div className="text-sm text-gray-600">
                    <strong>Analysis Period:</strong> {task.analysis_config.analysis_period_days} days
                  </div>
                  
                  {task.last_execution_status && (
                    <div className="mt-2 text-sm">
                      <strong>Last Status:</strong>{' '}
                      <span className={`font-medium ${
                        task.last_execution_status === 'success' 
                          ? 'text-green-600' 
                          : 'text-red-600'
                      }`}>
                        {task.last_execution_status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Scheduler;